import React, { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { adminUsersImportAction } from "../Action/action";

const TEMPLATE_HEADERS = [
  "Name",
  "Email",
  "Phone",
  "Password",
  "Relationship To Child",
  "Adult Age",
  "Status"
];

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function headerKey(header) {
  const h = normalizeHeader(header);
  if (["name", "full_name", "fullname"].includes(h)) return "name";
  if (["first_name", "firstname"].includes(h)) return "first_name";
  if (["last_name", "lastname"].includes(h)) return "last_name";
  if (["email", "email_address"].includes(h)) return "email";
  if (["phone", "mobile", "contact"].includes(h)) return "phone";
  if (["password"].includes(h)) return "password";
  if (["relationship_to_child", "relationship"].includes(h)) return "relationship_to_child";
  if (["adult_age", "age"].includes(h)) return "adult_age";
  if (["status", "is_active"].includes(h)) return "status";
  return null;
}

function parseWorkbook(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        if (!sheetName) {
          reject(new Error("The spreadsheet is empty."));
          return;
        }
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "", raw: false });
        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Could not read file."));
    reader.readAsArrayBuffer(file);
  });
}

function mapRow(raw, index) {
  const mapped = { row: index + 2 };
  Object.keys(raw || {}).forEach((key) => {
    const field = headerKey(key);
    if (!field) return;
    mapped[field] = raw[key];
  });
  if (!mapped.name) {
    const first = String(mapped.first_name || "").trim();
    const last = String(mapped.last_name || "").trim();
    mapped.name = `${first} ${last}`.trim();
  }
  mapped.email = String(mapped.email || "").trim();
  mapped.phone = String(mapped.phone || "").trim();
  mapped.password = String(mapped.password || "").trim();
  mapped.relationship_to_child = String(mapped.relationship_to_child || "").trim();
  mapped.adult_age = String(mapped.adult_age || "").trim();
  mapped.status = String(mapped.status || "").trim() || "Active";
  return mapped;
}

export default function UserImportModal({ open, onClose, onImported }) {
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [preview, setPreview] = useState([]);
  const [parseError, setParseError] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const resetState = () => {
    setFileName("");
    setPreview([]);
    setParseError("");
    setImporting(false);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (importing) return;
    resetState();
    onClose?.();
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.aoa_to_sheet([
      TEMPLATE_HEADERS,
      ["Alice Example", "alice@example.com", "0400000000", "", "Parent", "35", "Active"]
    ]);
    worksheet["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: Math.max(16, h.length + 4) }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
    XLSX.writeFile(workbook, "klc-users-import-template.xlsx");
  };

  const onPickFile = async (file) => {
    setParseError("");
    setResult(null);
    setPreview([]);
    setFileName(file?.name || "");
    if (!file) return;
    const lower = String(file.name || "").toLowerCase();
    if (!/\.(xlsx|xls|csv)$/.test(lower)) {
      setParseError("Use an Excel (.xlsx) or CSV file.");
      return;
    }
    try {
      const rawRows = await parseWorkbook(file);
      const mapped = rawRows.map(mapRow).filter((r) => r.email || r.name || r.phone);
      if (!mapped.length) {
        setParseError("No user rows found. Use the template headers (Name, Email, Phone, Password…).");
        return;
      }
      setPreview(mapped.slice(0, 300));
      if (mapped.length > 300) {
        setParseError("Only the first 300 rows will be imported.");
      }
    } catch (e) {
      setParseError(e?.message || "Could not read spreadsheet.");
    }
  };

  const submitImport = async () => {
    if (!preview.length || importing) return;
    setImporting(true);
    setParseError("");
    try {
      const res = await adminUsersImportAction({
        users: preview
      });
      if (!res?.success) throw new Error(res?.message || res?.msg || "Import failed.");
      setResult(res.data || null);
      toast.success(res.message || "Users imported", { toastId: "user-import" });
      onImported?.();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Import failed.";
      setParseError(msg);
      toast.error(msg, { toastId: "user-import" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay active" onClick={handleClose} role="dialog" aria-modal="true">
      <div className="modal modal-xl" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Import Users from Excel</h3>
          <button className="modal-close" type="button" onClick={handleClose} disabled={importing}>
            ✕
          </button>
        </div>
        <div className="modal-body">
          <p className="help-text" style={{ marginTop: 0 }}>
            Required column: <strong>Email</strong>. Optional: Name, Phone, Password, Relationship To Child, Adult Age, Status.
            Leave Password blank to auto-generate one.
          </p>
          <div className="flex items-center gap-3" style={{ marginBottom: 16, flexWrap: "wrap" }}>
            <button className="btn btn-outline" type="button" onClick={downloadTemplate} disabled={importing}>
              Download template
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              style={{ display: "none" }}
              onChange={(e) => onPickFile(e.target.files?.[0] || null)}
            />
          </div>
          <div
            className="upload-zone"
            role="button"
            tabIndex={0}
            onClick={() => !importing && fileInputRef.current?.click?.()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !importing) fileInputRef.current?.click?.();
            }}
          >
            <div className="upload-zone-icon">📥</div>
            <p>{fileName || "Click to choose an Excel file"}</p>
            <p className="upload-zone-hint">.xlsx or .csv · up to 300 rows</p>
          </div>

          {parseError ? (
            <div className="banner banner-warning" style={{ marginTop: 16 }}>
              <span className="banner-icon">⚠️</span>
              <div className="banner-text">{parseError}</div>
            </div>
          ) : null}

          {preview.length && !result ? (
            <div className="import-preview" style={{ marginTop: 16 }}>
              <p className="help-text">
                {preview.length} row{preview.length === 1 ? "" : "s"} ready to import
              </p>
              <div className="table-wrapper" style={{ maxHeight: 240, overflow: "auto" }}>
                <table>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 8).map((r) => (
                      <tr key={`${r.row}-${r.email}`}>
                        <td>{r.row}</td>
                        <td>{r.name || "—"}</td>
                        <td>{r.email || "—"}</td>
                        <td>{r.phone || "—"}</td>
                        <td>{r.password ? "From file" : "Auto-generate"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.length > 8 ? (
                <p className="help-text">Showing first 8 of {preview.length} rows.</p>
              ) : null}
            </div>
          ) : null}

          {result ? (
            <div style={{ marginTop: 16 }}>
              <p className="help-text">
                Created {result.created_count || 0}. Skipped {result.failed_count || 0}.
              </p>
              {Array.isArray(result.failed) && result.failed.length ? (
                <div className="table-wrapper" style={{ maxHeight: 200, overflow: "auto" }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Email</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.failed.map((f, idx) => (
                        <tr key={`${f.row}-${f.email}-${idx}`}>
                          <td>{f.row || "—"}</td>
                          <td>{f.email || "—"}</td>
                          <td>{f.reason || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" type="button" onClick={handleClose} disabled={importing}>
            {result ? "Close" : "Cancel"}
          </button>
          {!result ? (
            <button
              className="btn btn-primary"
              type="button"
              onClick={submitImport}
              disabled={importing || !preview.length}
            >
              {importing ? "Importing…" : `Import ${preview.length || ""} user${preview.length === 1 ? "" : "s"}`.trim()}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
